import { useEffect, useState } from 'react';
import type { UserSummary } from '@swg/contracts';
import { ROLE_LABELS, ROLES, type Role } from '@swg/domain';
import { ApiError, fetchUsers, updateUser } from '../api.js';
import { theme } from '../theme.js';
import { useUser } from '../user-context.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}

const PAGE_SIZE = 50;

export function Users() {
  const me = useUser();
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');
  const [error, setError] = useState<string | null>(null);
  /** P13 — paginering: cursor van de volgende pagina, of null bij de laatste. */
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  /** Waar de lijst nu op gefilterd is; los van het invoerveld. */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetchUsers({ limit: PAGE_SIZE, ...(appliedSearch ? { q: appliedSearch } : {}) })
      .then((r) => {
        if (cancelled) return;
        setUsers(r.users);
        setNextCursor(r.nextCursor);
        setTotal(r.total);
        setState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setState(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [appliedSearch]);

  if (me.role !== 'ADMIN' || state === 'forbidden') {
    return <p style={{ color: theme.color.muted }}>Alleen een beheerder heeft toegang tot gebruikersbeheer.</p>;
  }
  if (state === 'loading') return <p style={{ color: theme.color.muted }}>Laden…</p>;
  if (state === 'error' || !users) return <p style={{ color: theme.color.muted }}>De gebruikers konden niet worden geladen.</p>;

  /** Laadt de volgende pagina en zet die achter de huidige. */
  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const r = await fetchUsers({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        ...(appliedSearch ? { q: appliedSearch } : {}),
      });
      setUsers((prev) => [...(prev ?? []), ...r.users]);
      setNextCursor(r.nextCursor);
      setTotal(r.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Meer gebruikers laden mislukt.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function apply(id: string, patch: { role?: Role; isActive?: boolean }) {
    setError(null);
    try {
      const updated = await updateUser(id, patch);
      setUsers((prev) => (prev ? prev.map((u) => (u.id === id ? updated : u)) : prev));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Wijzigen mislukt.');
    }
  }

  const cell = { padding: '10px 12px', borderBottom: `1px solid ${theme.color.border}`, fontSize: 13.5, textAlign: 'left' as const };
  const th = { ...cell, color: theme.color.muted, fontWeight: 600, fontSize: 12 };

  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700 }}>Gebruikers</h1>
      <p style={{ margin: '0 0 14px', fontSize: 13.5, color: theme.color.muted }}>
        Beheer rollen en toegang. Nieuwe medewerkers krijgen bij eerste login automatisch de rol Kijker.
      </p>

      {/* Zoeken hoort bij paginering: met honderden gebruikers is doorbladeren
          naar één account geen werkbare manier om een rol te wijzigen. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setAppliedSearch(search.trim());
        }}
        style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op naam of e-mail"
          style={{
            flex: '1 1 240px',
            padding: '8px 10px',
            borderRadius: 8,
            border: `1px solid ${theme.color.borderStrong}`,
            fontSize: 13.5,
            fontFamily: 'inherit',
          }}
        />
        <button
          type="submit"
          style={{
            background: theme.color.primary,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13.5,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Zoeken
        </button>
        {appliedSearch && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setAppliedSearch('');
            }}
            style={{
              background: theme.color.surface,
              border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 13.5,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Wissen
          </button>
        )}
      </form>

      {error && (
        <div style={{ background: '#FBE6E6', border: '1px solid #EFC2C2', color: '#A82A2A', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ background: theme.color.surface, border: `1px solid ${theme.color.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Naam</th>
              <th style={th}>E-mail</th>
              <th style={th}>Rol</th>
              <th style={th}>Actief</th>
              <th style={th}>Laatste login</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={cell}>
                  {u.displayName}
                  {u.id === me.id && <span style={{ color: theme.color.subtle, fontSize: 12 }}> (jij)</span>}
                </td>
                <td style={cell}>{u.email}</td>
                <td style={cell}>
                  <select
                    value={u.role}
                    onChange={(e) => void apply(u.id, { role: e.target.value as Role })}
                    style={{ padding: '6px 8px', fontSize: 13, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 6, fontFamily: 'inherit' }}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={cell}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={u.isActive} onChange={(e) => void apply(u.id, { isActive: e.target.checked })} />
                    {u.isActive ? 'Actief' : 'Inactief'}
                  </label>
                </td>
                <td style={cell}>{formatDate(u.lastLoginAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: theme.color.muted }}>
          {users.length} van {total} gebruiker{total === 1 ? '' : 's'}
          {appliedSearch ? ` met "${appliedSearch}"` : ''}
        </span>
        {nextCursor && (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            style={{
              background: theme.color.surface,
              border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 8,
              padding: '7px 14px',
              fontSize: 13,
              cursor: loadingMore ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {loadingMore ? 'Laden…' : 'Meer laden'}
          </button>
        )}
      </div>

      {users.length === 0 && (
        <p style={{ margin: '12px 0 0', fontSize: 13.5, color: theme.color.muted }}>
          Geen gebruikers gevonden{appliedSearch ? ` voor "${appliedSearch}"` : ''}.
        </p>
      )}
    </div>
  );
}
